import { defineStore } from 'pinia';
import { featureFlagSlugs } from '@/constants/featureFlagSlugs';
import useAcvDatadog from '@/composables/useAcvDatadog';

const { addRumError } = useAcvDatadog();

export const useFeatureFlagsStore = defineStore({
  id: 'featureFlagsStore',
  state: () => {
    return {
      flags: {},
    };
  },
  actions: {
    async fetchAllFlags() {
      const featureFlagSlugArray = Object.values(featureFlagSlugs);
      if (featureFlagSlugArray.length === Object.keys(this.flags).length) {
        return this.flags;
      }
      const body = {
        method: 'POST',
        body: { featureFlags: featureFlagSlugArray },
      };

      try {
        const { $api } = useNuxtApp();
        let data = null;
        let error = null;
        try {
          data = await $api(`/api/feature-flags`, body);
        } catch (err) {
          error = err;
        }

        if (error) {
          const statusCode = error.statusCode;
          const errorData = error.data;

          /**
           * Temporarily remove error reporting for users that are authorized but not yet fully signed up
           * This is necessary to support the Magic Link epic because we need to fetch FFs before a user's role has been validated
           * Once the feature has been deployed and turned on for all users, this status code check can be removed
           */

          if (statusCode !== 404) {
            // http error from server

            addRumError('Promise resolved. Unable to fetch feature flags.', {
              error: error,
              featureFlagErrors: errorData?.errors,
              requestBody: body,
              statusCode,
            });
            return;
          }
        }

        if (data) {
          this.flags = data.data;
        }
      } catch (error) {
        // unable to complete request error

        addRumError('Promise rejected. Unable to fetch feature flags.', {
          error: error,
          requestBody: body,
          statusCode,
        });
      }
    },
    async fetchFlagByName(flagName) {
      if (!flagName) {
        return;
      }

      if (this.getByFlagName(flagName) !== undefined) {
        return this.getByFlagName(flagName);
      }

      try {
        const { $api } = useNuxtApp();
        const data = await $api(`/api/feature-flag/${flagName}`);

        this.flags = {
          ...this.flags,
          [flagName]: data.data,
        };
        return this.getByFlagName(flagName);
      } catch (error) {
        addRumError('Failed to fetch feature flag:', {
          flagName,
          error,
          flags: this.flags,
        });
      }
    },
  },
  getters: {
    getByFlagName: (state) => {
      return (flagName) => state.flags[flagName];
    },
  },
});
